import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SetPreferenceInput } from '@maker-toolchest/shared';
import { preferencesApi } from './api';

const USER_PREFERENCES_KEY = 'user-preferences';
const WORKSPACE_PREFERENCES_KEY = 'workspace-preferences';

// ========================================================================
// USER PREFERENCES
// ========================================================================

/**
 * Hook to fetch all user preferences, optionally filtered by prefix
 */
export function useUserPreferences(prefix?: string) {
  return useQuery({
    queryKey: [USER_PREFERENCES_KEY, prefix],
    queryFn: () => preferencesApi.getUserPreferences(prefix),
  });
}

/**
 * Hook to fetch a single user preference with a default value
 */
export function useUserPreference<T = any>(key: string, defaultValue?: T) {
  return useQuery({
    queryKey: [USER_PREFERENCES_KEY, key],
    queryFn: () => preferencesApi.getUserPreference(key),
    select: (value) => (value !== null ? value : defaultValue) as T,
  });
}

/**
 * Hook to set a single user preference
 */
export function useSetUserPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: any }) =>
      preferencesApi.setUserPreference(key, value),
    onSuccess: (_, variables) => {
      // Invalidate all user preferences and the specific key
      queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_KEY, variables.key] });
    },
  });
}

/**
 * Hook to set multiple user preferences at once
 */
export function useSetUserPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: SetPreferenceInput[]) =>
      preferencesApi.setUserPreferences(preferences),
    onSuccess: () => {
      // Invalidate all user preferences
      queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_KEY] });
    },
  });
}

/**
 * Hook to delete a user preference
 */
export function useDeleteUserPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => preferencesApi.deleteUserPreference(key),
    onSuccess: () => {
      // Invalidate all user preferences
      queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_KEY] });
    },
  });
}

// ========================================================================
// WORKSPACE PREFERENCES
// ========================================================================

/**
 * Hook to fetch all workspace preferences, optionally filtered by prefix
 */
export function useWorkspacePreferences(prefix?: string) {
  return useQuery({
    queryKey: [WORKSPACE_PREFERENCES_KEY, prefix],
    queryFn: () => preferencesApi.getWorkspacePreferences(prefix),
  });
}

/**
 * Hook to fetch a single workspace preference with a default value
 */
export function useWorkspacePreference<T = any>(key: string, defaultValue?: T) {
  return useQuery({
    queryKey: [WORKSPACE_PREFERENCES_KEY, key],
    queryFn: () => preferencesApi.getWorkspacePreference(key),
    select: (value) => (value !== null ? value : defaultValue) as T,
  });
}

/**
 * Hook to set a single workspace preference (requires OWNER/ADMIN role)
 */
export function useSetWorkspacePreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: any }) =>
      preferencesApi.setWorkspacePreference(key, value),
    onSuccess: (_, variables) => {
      // Invalidate all workspace preferences and the specific key
      queryClient.invalidateQueries({ queryKey: [WORKSPACE_PREFERENCES_KEY] });
      queryClient.invalidateQueries({ queryKey: [WORKSPACE_PREFERENCES_KEY, variables.key] });
    },
  });
}

/**
 * Hook to set multiple workspace preferences at once (requires OWNER/ADMIN role)
 */
export function useSetWorkspacePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: SetPreferenceInput[]) =>
      preferencesApi.setWorkspacePreferences(preferences),
    onSuccess: () => {
      // Invalidate all workspace preferences
      queryClient.invalidateQueries({ queryKey: [WORKSPACE_PREFERENCES_KEY] });
    },
  });
}

/**
 * Hook to delete a workspace preference (requires OWNER/ADMIN role)
 */
export function useDeleteWorkspacePreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => preferencesApi.deleteWorkspacePreference(key),
    onSuccess: () => {
      // Invalidate all workspace preferences
      queryClient.invalidateQueries({ queryKey: [WORKSPACE_PREFERENCES_KEY] });
    },
  });
}
